/**
 * Геометрические утилиты: hit-testing, bounding boxes, упрощение путей.
 * Чистые функции, тестируются юнитами.
 */
import type {
  NumberShape,
  Point,
  Shape,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Базовая арифметика
// ─────────────────────────────────────────────────────────────────────────────
export function dist(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

const DEG = Math.PI / 180;

export function rotatePoint(p: Point, center: Point, deg: number): Point {
  if (deg === 0) return p;
  const a = deg * DEG;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

/** Преобразовать экранные координаты в координаты изображения. */
export function screenToImage(
  p: Point,
  scale: number,
  tx: number,
  ty: number,
): Point {
  return { x: (p.x - tx) / scale, y: (p.y - ty) / scale };
}

/** Преобразовать координаты изображения в экранные. */
export function imageToScreen(
  p: Point,
  scale: number,
  tx: number,
  ty: number,
): Point {
  return { x: p.x * scale + tx, y: p.y * scale + ty };
}

// ─────────────────────────────────────────────────────────────────────────────
// Bounding box
// ─────────────────────────────────────────────────────────────────────────────
export interface BBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function normalizeRect(x: number, y: number, x2: number, y2: number): BBox {
  return {
    x: Math.min(x, x2),
    y: Math.min(y, y2),
    w: Math.abs(x2 - x),
    h: Math.abs(y2 - y),
  };
}

/** Bounding box shape без учёта rotation (для большинства инструментов достаточно). */
export function shapeBBox(shape: Shape): BBox {
  switch (shape.type) {
    case 'arrow':
      // Для змейки (path) bbox охватывает все точки пути + концы.
      if (shape.path && shape.path.length >= 2) {
        return pointsBBox(shape.path);
      }
      return normalizeRect(shape.x, shape.y, shape.x2, shape.y2);
    case 'rect': {
      const rs = shape as import('./types').RectShape;
      if (rs.strokeEnabled) {
        const sw = rs.strokeWidth;
        return {
          x: rs.x - sw,
          y: rs.y - sw,
          w: rs.w + 2 * sw,
          h: rs.h + 2 * sw,
        };
      }
      return { x: rs.x, y: rs.y, w: rs.w, h: rs.h };
    }
    case 'blur':
      return { x: shape.x, y: shape.y, w: shape.w, h: shape.h };
    case 'number': {
      const r = shape.radius;
      const stroke = shape.strokeEnabled ? shape.strokeWidth : 0;
      const totalR = r + stroke;
      return { x: shape.x - totalR, y: shape.y - totalR, w: totalR * 2, h: totalR * 2 };
    }
    case 'text': {
      // Должно совпадать с формулой подложки в ShapeView/export, иначе рамка
      // выделения «съедет» относительно текста.
      const lines = shape.text.split('\n');
      const maxLen = Math.max(1, ...lines.map((l) => l.length));
      const lh = shape.fontSize * 1.25;
      const w = maxLen * shape.fontSize * 0.6 + 16;
      const h = lines.length * lh + 12;
      return { x: shape.x, y: shape.y, w, h };
    }
    case 'pencil':
      return pointsBBox(shape.points);
    default:
      return { x: 0, y: 0, w: 0, h: 0 };
  }
}

export function pointsBBox(points: Point[]): BBox {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// ─────────────────────────────────────────────────────────────────────────────
// Hit-testing: попадание точки в shape
// ─────────────────────────────────────────────────────────────────────────────
export function pointInRect(p: Point, bb: BBox): boolean {
  return p.x >= bb.x && p.x <= bb.x + bb.w && p.y >= bb.y && p.y <= bb.y + bb.h;
}

export function pointInCircle(p: Point, center: Point, r: number): boolean {
  return dist(p, center) <= r;
}

/** Расстояние от точки P до отрезка AB. */
export function distToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return dist(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = clamp(t, 0, 1);
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Попадание точки в полилинию (с допуском). */
export function distToPolyline(p: Point, points: Point[], closed: boolean): number {
  if (points.length === 0) return Infinity;
  if (points.length === 1) return dist(p, points[0]);
  let min = Infinity;
  for (let i = 0; i < points.length - 1; i++) {
    min = Math.min(min, distToSegment(p, points[i], points[i + 1]));
  }
  if (closed && points.length > 2) {
    min = Math.min(min, distToSegment(p, points[points.length - 1], points[0]));
  }
  return min;
}

/** Проверка «точка внутри многоугольника» (ray casting). */
export function pointInPolygon(p: Point, poly: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    const intersect =
      yi > p.y !== yj > p.y &&
      p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Hit-test произвольного shape: вернёт true, если точка (в координатах
 * изображения) попадает в shape с учётом толщины контура.
 */
export function hitTest(shape: Shape, p: Point): boolean {
  const tol = Math.max(shape.strokeWidth / 2 + 4, 6);
  switch (shape.type) {
    case 'arrow': {
      // Змейка: hit-test по всем сегментам пути (тело).
      if (shape.path && shape.path.length >= 2) {
        if (distToPolyline(p, shape.path, false) <= tol) return true;
      }
      // Прямая: 6-точечный полигон по arrowVertices.
      if (distToSegment(p, { x: shape.x, y: shape.y }, { x: shape.x2, y: shape.y2 }) <= tol) return true;
      const v = arrowVertices(
        { x: shape.x, y: shape.y },
        { x: shape.x2, y: shape.y2 },
        shape.headSize,
      );
      return pointInPolygon(p, [v.to, v.B, v.wingUp, v.from, v.wingDn, v.F]);
    }
    case 'rect': {
      const bb = { x: shape.x, y: shape.y, w: shape.w, h: shape.h };
      if (shape.fillEnabled && pointInRect(p, bb)) return true;
      // допуск по периметру
      return nearRectEdge(p, bb, tol);
    }
    case 'blur':
      return pointInRect(p, { x: shape.x, y: shape.y, w: shape.w, h: shape.h });
    case 'number':
      return pointInCircle(p, { x: shape.x, y: shape.y }, shape.radius);
    case 'text':
      return pointInRect(p, shapeBBox(shape));
    case 'pencil': {
      const d = distToPolyline(p, shape.points, shape.closed);
      if (d <= tol) return true;
      return shape.closed && shape.fillEnabled && pointInPolygon(p, shape.points);
    }

    default:
      return false;
  }
}

function nearRectEdge(p: Point, bb: BBox, tol: number): boolean {
  const { x, y, w, h } = bb;
  const inOuter = p.x >= x - tol && p.x <= x + w + tol && p.y >= y - tol && p.y <= y + h + tol;
  if (!inOuter) return false;
  const inInner = p.x >= x + tol && p.x <= x + w - tol && p.y >= y + tol && p.y <= y + h - tol;
  return !inInner;
}

// ─────────────────────────────────────────────────────────────────────────────
// Упрощение и сглаживание путей (карандаш/лассо)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Алгоритм Ramer–Douglas–Peucker: упрощает полилинию, оставляя ключевые точки.
 * Используется для уменьшения объёма данных карандаша.
 */
export function simplifyPath(points: Point[], epsilon = 1.5): Point[] {
  if (points.length <= 2) return points.slice();

  // RDP рекурсивно
  const keep = new Array(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;

  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length > 0) {
    const [start, end] = stack.pop()!;
    let maxDist = 0;
    let index = start;
    const a = points[start];
    const b = points[end];
    for (let i = start + 1; i < end; i++) {
      const d = distToSegment(points[i], a, b);
      if (d > maxDist) {
        maxDist = d;
        index = i;
      }
    }
    if (maxDist > epsilon) {
      keep[index] = true;
      stack.push([start, index]);
      stack.push([index, end]);
    }
  }
  return points.filter((_, i) => keep[i]);
}

/**
 * Сглаживание Catmull-Rom → массив точек Безье для красивой векторной линии.
 * Возвращает строку path для SVG.
 */
export function catmullRomPath(points: Point[], closed = false): string {
  if (points.length < 2) {
    if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
    return '';
  }
  const pts = points.slice();
  let d = `M ${pts[0].x} ${pts[0].y}`;
  const n = pts.length;
  const seg = (i: number): [Point, Point, Point, Point] => {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    return [p0, p1, p2, p3];
  };
  const bezier = (p0: Point, p1: Point, p2: Point, p3: Point): string => {
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    return `C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`;
  };
  const last = closed ? n : n - 1;
  for (let i = 0; i < last; i++) {
    const [p0, p1, p2, p3] = seg(i);
    d += ` ${bezier(p0, p1, p2, p3)}`;
  }
  if (closed) d += ' Z';
  return d;
}

/** Обычная полилиния как SVG path. */
export function polylinePath(points: Point[], closed = false): string {
  if (points.length === 0) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L ${points[i].x} ${points[i].y}`;
  }
  if (closed) d += ' Z';
  return d;
}

// ─────────────────────────────────────────────────────────────────────────────
// Геометрия стрелки
// ─────────────────────────────────────────────────────────────────────────────
export interface ArrowGeom {
  /**
   * Полная заливка стрелки (тело + наконечник) как единый замкнутый path.
   * Стрелка острая с обоих концов: хвост сужается в точку, наконечник — в точку.
   * Толщина тела = strokeWidth (он же «Размер»).
   */
  fillPath: string;
  /**
   * Центральная ось (для отрисовки пунктира/точек поверх заливки).
   * Укорочена с обеих сторон на половину толщины, чтобы точки не вылезали
   * за острые концы.
   */
  axisPath: string;
  /** Stroke-dasharray для пунктира/точек (или undefined для сплошной). */
  dashArray?: string;
}

/**
 * Геометрия стрелки — форма по эталонному arrow.svg (6 точек).
 *
 *      to ●  ← нос (одна точка, куда указывает стрелка)
 *          ╲
 *           ╲   (тело сужается от шейки к носу)
 *            ╲
 *         B ●      ← верх шейки
 *           │
 * wingUp ●   │    ← верхнее крыло (торчит вбок от оси)
 *           │
 *      from ●─────← хвост (точка старта, одна острая точка, откуда тянул мышкой)
 *           │
 * wingDn ●   │    ← нижнее крыло
 *           │
 *         F ●      ← низ шейки
 *            ╲
 *             ╲   (симметрично)
 *
 * Порядок вершин полигона: to → B → wingUp → from → wingDn → F → to.
 *
 * Пропорции выведены из arrow.svg (длина оси от from до to = 44.84):
 *  - крылья на расстоянии WING_AT ≈ 0.175 длины от from;
 *  - размах крыла вбок ≈ 0.175 длины (крыло у шейки, не у носа);
 *  - ширина шейки ≈ 0.09 длины (тело тонкое между шейкой и носом).
 *
 * headSize («Размер») масштабирует всю эту структуру. Длина стрелки
 * (from→to) задаётся перетаскиванием мышью и НЕ зависит от headSize.
 */
const WING_AT = 0.175; // крылья на расстоянии от from (доля длины оси)
const WING_OUT = 0.175; // выступ крыла вбок от оси (доля длины оси)
const NECK_HALF = 0.045; // полуширина шейки (доля длины оси)
const NECK_AT = 0.158; // шейка чуть ближе к from, чем крылья

/**
 * 6 вершин стрелки (to, B, wingUp, from, wingDn, F) — общая геометрия
 * для отрисовки и hit-test.
 */
export function arrowVertices(
  from: Point,
  to: Point,
  headSize: number,
): { to: Point; B: Point; wingUp: Point; from: Point; wingDn: Point; F: Point; neckAxis: Point } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len; // единичный вдоль оси
  const uy = dy / len;
  const nx = -uy; // нормаль
  const ny = ux;

  // Ползунок «Размер» идёт в диапазоне 18..36; для совместимости с прежним
  // визуальным масштабом (180..360) умножаем на 10. Так small integer-значение
  // в UI даёт крупную стрелку.
  const h = headSize * 10;

  // h масштабирует пропорции; все размеры — доля длины × h.
  const wingOut = h * WING_OUT;
  const neckHalf = h * NECK_HALF;
  const wingAt = h * WING_AT; // расстояние от from вдоль оси
  const neckAt = h * NECK_AT;

  const at = (dist: number) => ({ x: from.x + ux * dist, y: from.y + uy * dist });
  const offset = (p: Point, side: number) => ({ x: p.x + nx * side, y: p.y + ny * side });

  const wingAxis = at(wingAt);
  const neckAxis = at(neckAt);
  return {
    to,
    B: offset(neckAxis, -neckHalf), // верх шейки
    wingUp: offset(wingAxis, -wingOut), // верхнее крыло
    from,
    wingDn: offset(wingAxis, wingOut), // нижнее крыло
    F: offset(neckAxis, neckHalf), // низ шейки
    neckAxis,
  };
}

export function arrowGeometry(
  from: Point,
  to: Point,
  _headStyle: 'arrow' | 'line' | 'circle',
  headSize: number,
  options?: {
    strokeWidth?: number;
    dash?: 'solid' | 'dashed' | 'dotted';
    /** Произвольный путь (змейка). Если задан и непустой — рисуется он. */
    path?: Point[];
  },
): ArrowGeom {
  const strokeWidth = options?.strokeWidth ?? 7;
  const dash = options?.dash ?? 'solid';

  // Змейка: тело идёт по пути, голова (6 точек) строится по направлению в конце.
  if (options?.path && options.path.length >= 2) {
    const axisPts = smoothFreehand(options.path);
    axisPts[0] = { ...from };
    axisPts[axisPts.length - 1] = { ...to };
    const n = axisPts.length;
    const endDir = dirAt(axisPts, n - 1);
    // «from» для головы — точка на оси, отступая от to на длину головы (≈ headSize×WING_AT×~1.5)
    const headFrom = {
      x: to.x - endDir.dx * headSize * 0.6,
      y: to.y - endDir.dy * headSize * 0.6,
    };
    const v = arrowVertices(headFrom, to, headSize);
    // боковые вдоль пути (тонкое тело)
    const normal = (d: { dx: number; dy: number }) => {
      const l = Math.hypot(d.dx, d.dy) || 1;
      return { nx: -d.dy / l, ny: d.dx / l };
    };
    const half = strokeWidth / 2;
    const left: Point[] = [];
    const right: Point[] = [];
    for (let i = 1; i < n - 1; i++) {
      const p = axisPts[i];
      const d = dirAt(axisPts, i);
      const nn = normal(d);
      left.push({ x: p.x + nn.nx * half, y: p.y + nn.ny * half });
      right.push({ x: p.x - nn.nx * half, y: p.y - nn.ny * half });
    }
    let fillPath = `M ${from.x} ${from.y}`;
    for (const p of left) fillPath += ` L ${p.x} ${p.y}`;
    fillPath += ` L ${v.B.x} ${v.B.y} L ${v.wingUp.x} ${v.wingUp.y} L ${to.x} ${to.y}`;
    fillPath += ` L ${v.wingDn.x} ${v.wingDn.y} L ${v.F.x} ${v.F.y}`;
    for (let i = right.length - 1; i >= 0; i--) fillPath += ` L ${right[i].x} ${right[i].y}`;
    fillPath += ' Z';
    return { fillPath, axisPath: catmullRomPath(axisPts.slice(0, n - 1).concat([v.neckAxis]), false) };
  }

  // Прямая стрелка: 6-точечный полигон по arrowVertices.
  const v = arrowVertices(from, to, headSize);
  const fillPath =
    `M ${v.to.x} ${v.to.y}` +
    ` L ${v.B.x} ${v.B.y}` +
    ` L ${v.wingUp.x} ${v.wingUp.y}` +
    ` L ${v.from.x} ${v.from.y}` +
    ` L ${v.wingDn.x} ${v.wingDn.y}` +
    ` L ${v.F.x} ${v.F.y}` +
    ` Z`;

  // Ось для пунктира/точек: от точки у from до шейки.
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const axisPath = `M ${from.x + (dx / len) * strokeWidth} ${from.y + (dy / len) * strokeWidth} L ${v.neckAxis.x} ${v.neckAxis.y}`;

  let dashArray: string | undefined;
  if (dash === 'dashed') {
    dashArray = '14 10';
  } else if (dash === 'dotted') {
    const dot = Math.max(10, strokeWidth);
    dashArray = `0.1 ${dot}`;
  }

  return { fillPath, axisPath, dashArray };
}

/** Направление (единичный вектор) оси в точке i. */
function dirAt(pts: Point[], i: number): { dx: number; dy: number } {
  const n = pts.length;
  let ax: number;
  let ay: number;
  if (i === 0) {
    ax = pts[1].x - pts[0].x;
    ay = pts[1].y - pts[0].y;
  } else if (i === n - 1) {
    ax = pts[n - 1].x - pts[n - 2].x;
    ay = pts[n - 1].y - pts[n - 2].y;
  } else {
    ax = pts[i + 1].x - pts[i - 1].x;
    ay = pts[i + 1].y - pts[i - 1].y;
  }
  const len = Math.hypot(ax, ay) || 1;
  return { dx: ax / len, dy: ay / len };
}

/**
 * Сглаживание произвольного набора точек (для змейки-стрелки).
 * Комбинация упрощения (RDP) + Catmull-Rom даёт мягкий переход без зигзагов.
 */
function smoothFreehand(points: Point[]): Point[] {
  return simplifyPath(points, 2);
}

// ─────────────────────────────────────────────────────────────────────────────
// Перемещение shape на вектор (dx, dy) в координатах изображения
// ─────────────────────────────────────────────────────────────────────────────
export function translateShape(shape: Shape, dx: number, dy: number): Shape {
  switch (shape.type) {
    case 'arrow':
      return {
        ...shape,
        x: shape.x + dx,
        y: shape.y + dy,
        x2: shape.x2 + dx,
        y2: shape.y2 + dy,
        path: shape.path ? shape.path.map((p) => ({ x: p.x + dx, y: p.y + dy })) : shape.path,
      };
    case 'rect':
    case 'blur':
      return { ...shape, x: shape.x + dx, y: shape.y + dy };
    case 'number':
      return {
        ...shape,
        x: shape.x + dx,
        y: shape.y + dy,
        arrowTarget: shape.arrowTarget ? { x: shape.arrowTarget.x + dx, y: shape.arrowTarget.y + dy } : undefined,
      };
    case 'text':
      return { ...shape, x: shape.x + dx, y: shape.y + dy };
    case 'pencil':
      return { ...shape, points: shape.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
    default:
      return shape;
  }
}

/** Отступ стрелки нумерации от края кружка (поверх контура). */
export const NUMBER_ARROW_GAP = 20;

/**
 * Расчёт КОНЦА (носа) стрелки нумерации по углу.
 * Нос расположен снаружи кружка на расстоянии: radius + контур + gap + arrowLength.
 */
export function numberArrowEnd(shape: NumberShape): Point {
  const a = shape.arrowAngle * DEG;
  const stroke = shape.strokeEnabled ? shape.strokeWidth : 0;
  // В режиме 'angle' длина arrowLength — это длина самой стрелки (от gap до носа).
  // В режиме 'target' цель задаётся явно — возвращаем её.
  if (shape.arrowMode === 'angle' || !shape.arrowTarget) {
    const dist = shape.radius + stroke + NUMBER_ARROW_GAP + shape.arrowLength;
    return {
      x: shape.x + dist * Math.cos(a),
      y: shape.y + dist * Math.sin(a),
    };
  }
  return shape.arrowTarget;
}

/**
 * Расчёт НАЧАЛА (хвоста) стрелки нумерации — точка снаружи кружка,
 * отступая от края на gap (20px) поверх контура. Стрелка не прилегает
 * впритык к кружку.
 */
export function numberArrowStart(shape: NumberShape): Point {
  const a = shape.arrowAngle * DEG;
  const stroke = shape.strokeEnabled ? shape.strokeWidth : 0;
  const dist = shape.radius + stroke + NUMBER_ARROW_GAP;
  return {
    x: shape.x + dist * Math.cos(a),
    y: shape.y + dist * Math.sin(a),
  };
}

/** Маркер-номер/восклицательный знак над фигурой — позиция (центр сверху). */
export function markerPosition(bb: BBox): Point {
  return { x: bb.x + bb.w / 2, y: bb.y };
}
